/**
 * Utility functions for building rate card API payloads
 */

/**
 * `tapApi.format()` uses each format's label/code as option `value`, so the form
 * already stores API strings. Normalize to a comma-separated string.
 *
 * @param {string|string[]|number|undefined|null} v - From tier alias / form
 * @returns {string|undefined}
 */
function joinTierAliasFormatForApi(v) {
  if (v == null) {
    return v;
  }
  if (Array.isArray(v)) {
    return v
      .filter((x) => x != null && String(x).trim() !== "")
      .map((x) => String(x).trim())
      .join(",");
  }
  if (typeof v === "string") {
    return v
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean)
      .join(",");
  }
  return String(v);
}

/**
 * Helper to get tierName for a given row and alias index
 * Pattern: First alias uses simple numbers (1, 2, 3...)
 * Subsequent aliases use 101, 102... then 1001, 1002... etc.
 * @param {number} rowIdx - The row index (0-based)
 * @param {number} aliasIdx - The alias index (0-based)
 * @returns {number} - The tier name
 */
export function getTierName(rowIdx, aliasIdx) {
  if (aliasIdx === 0) {
    return rowIdx + 1;
  }
  return Number(`1${"0".repeat(aliasIdx)}${rowIdx + 1}`);
}

/**
 * Builds the tier aliases array for the API payload (create mode)
 * @param {Object} formData - The form data
 * @returns {Array} - Formatted tier aliases for the API
 */
export function buildTierAliasesPayload(formData) {
  const tierAliases = Array.isArray(formData.tierAliases) ? formData.tierAliases : [];
  const tierRows = Array.isArray(formData.tierRows) ? formData.tierRows : [];

  return tierAliases.map((alias, aliasIdx) => {
    const tierAliasFormat = joinTierAliasFormatForApi(alias.tierAliasFormat);

    const tiers = [];
    tierRows.forEach((row, rowIdx) => {
      const aliasCol = row.aliasColumns?.[aliasIdx];
      if (aliasCol) {
        row.priceColumns?.forEach((col) => {
          tiers.push({
            id: {
              tapWindowId: col.tapWindowId,
              tierNumber: row.tierNumber,
            },
            tierName: aliasCol.tierName,
            price: col.price,
            sortOrder: rowIdx + 2,
          });
        });
      }
    });

    return {
      ...alias,
      tierAliasFormat,
      tiers,
    };
  });
}

/**
 * Extracts window sequence data without the nested windows array
 * @param {Object} windowSequence - The window sequence option object
 * @returns {Object|undefined} - Window sequence data for the API
 */
export function extractWindowSequenceData(windowSequence) {
  const data = windowSequence?.data;
  if (!data) return undefined;

  if (data.windows) {
    const { windows, ...rest } = data;
    return rest;
  }
  return data;
}

/**
 * Builds the complete rate card payload for create API
 * @param {Object} formData - The form data
 * @returns {Object} - The API payload
 */
export function buildCreateRateCardPayload(formData) {
  return {
    windows: formData.windows,
    status: formData.status,
    isTpr: formData.isTpr,
    rateCardName: formData.rateCardName,
    windowSequence: extractWindowSequenceData(formData.windowSequence),
    tierAliases: buildTierAliasesPayload(formData),
    currency: formData.currency?.label,
    priceType: formData.priceType?.label,
    revShareType: formData.revShareType?.label,
    windowSequenceId: formData.windowSequence?.value,
  };
}

/**
 * Converts currency to string format for API
 * @param {string|Object} currency - Currency value (string or object)
 * @returns {string} - Currency string
 */
function normalizeCurrency(currency) {
  if (!currency) return currency;
  if (typeof currency === "object") {
    return currency.searchableTitle || currency.label;
  }
  return currency;
}

/**
 * Normalizes status - converts NEEDS_ATTENTION variants to ACTIVE
 * @param {string} status - The status value
 * @returns {string} - Normalized status
 */
function normalizeStatus(status) {
  if (status === "NEEDS_ATTENTION" || status === "NEEDSATTENTION") {
    return "ACTIVE";
  }
  return status;
}

/**
 * Transforms tier aliases for edit API payload
 * - Converts tierAliasFormat from array to comma-separated string
 * - Cleans price values (removes commas)
 * @param {Array} tierAliases - The tier aliases array
 * @returns {Array} - Transformed tier aliases
 */
function transformEditTierAliases(tierAliases) {
  if (!Array.isArray(tierAliases)) return tierAliases;

  return tierAliases.map((alias) => {
    const updatedAlias = { ...alias };

    updatedAlias.tierAliasFormat = joinTierAliasFormatForApi(
      updatedAlias.tierAliasFormat,
    );

    if (Array.isArray(updatedAlias.tiers)) {
      updatedAlias.tiers = updatedAlias.tiers.map((tier) => ({
        ...tier,
        price: tier.price ? String(tier.price).replace(",", "") : tier.price,
      }));
    }

    return updatedAlias;
  });
}

/**
 * Builds the complete rate card payload for edit/update API
 * @param {Object} selectedRateCard - The original rate card data from API
 * @returns {Object} - The API payload for update
 */
export function buildEditRateCardPayload(selectedRateCard) {
  return {
    ...selectedRateCard,
    status: normalizeStatus(selectedRateCard.status),
    currency: normalizeCurrency(selectedRateCard.currency),
    tierAliases: transformEditTierAliases(selectedRateCard.tierAliases),
  };
}
